import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { user, chatbots, documents } from '../services/api';
import Analytics from './Analytics';
import Conversations from './Conversations';
import heroBackground from '../assets/hero-background.webp';
import logo from '../assets/logo.png';

function Dashboard({ setAuth }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('chatbots');
  const [allChatbots, setAllChatbots] = useState([]);
  const [selectedChatbot, setSelectedChatbot] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [notification, setNotification] = useState(null);
  const [userProfile, setUserProfile] = useState(null);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    if (type === 'success') {
      setTimeout(() => setNotification(null), 4000);
    }
  };

  const clearNotification = () => {
    setNotification(null);
  };
  
  // Form states
  const [chatbotName, setChatbotName] = useState('My Support Bot');
  const [chatbotColor, setChatbotColor] = useState('#667eea');
  const [welcomeMessage, setWelcomeMessage] = useState('Hi! How can I help you today?');
  const [showNewBotForm, setShowNewBotForm] = useState(false);

  const loadUserProfile = useCallback(async () => {
    try {
      const response = await user.getProfile();
      setUserProfile(response.data);
    } catch (error) {
      console.error('Error loading user profile:', error);
    }
  }, []);

  const loadChatbots = useCallback(async (updateSelected = false) => {
    try {
      const response = await chatbots.getAll();
      setAllChatbots(response.data);
      if (response.data.length > 0) {
        if (updateSelected) {
          const current = selectedChatbot;
          if (!current) {
            setSelectedChatbot(response.data[0]);
          } else {
            const updated = response.data.find(bot => bot.id === current.id);
            if (updated) setSelectedChatbot(updated);
          }
        } else if (!selectedChatbot) {
          setSelectedChatbot(response.data[0]);
        }
      }
    } catch (error) {
      console.error('Error loading chatbots:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedChatbot]);

  useEffect(() => {
    loadUserProfile();
    loadChatbots();
  }, [loadUserProfile, loadChatbots]);

  useEffect(() => {
    if (selectedChatbot) {
      loadDocuments(selectedChatbot.id);
      setChatbotName(selectedChatbot.name);
      setChatbotColor(selectedChatbot.color);
      setWelcomeMessage(selectedChatbot.welcome_message);
    }
  }, [selectedChatbot]);


  const loadDocuments = async (chatbotId) => {
    try {
      const response = await documents.getAll(chatbotId);
      setFiles(response.data);
    } catch (error) {
      console.error('Error loading documents:', error);
    }
  };

  const handleCreateChatbot = async () => {
    try {
      const response = await chatbots.create({
        name: chatbotName || 'New Chatbot',
        color: chatbotColor,
        welcomeMessage: welcomeMessage
      });
      setAllChatbots([response.data, ...allChatbots]);
      setSelectedChatbot(response.data);
      setShowNewBotForm(false);
      showNotification('Chatbot created successfully!');
    } catch (error) {
      showNotification('Failed to create chatbot: ' + (error.response?.data?.error || 'Unknown error'), 'error');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedChatbot) return;
    try {
      const response = await documents.upload(selectedChatbot.id, file);
      setFiles([response.data, ...files]);
      showNotification('Document uploaded successfully!');
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Unknown error';
      if (errorMsg.includes('Failed to parse PDF')) {
        showNotification('PDF upload failed. Try converting to TXT or DOCX format, or use a text-based PDF (not scanned images).', 'error');
      } else {
        showNotification('Failed to upload: ' + errorMsg, 'error');
      }
    }
    e.target.value = '';
  };

  const [deleteModal, setDeleteModal] = useState({ show: false, docId: null, filename: '' });

  const handleDeleteFile = async (docId, filename) => {
    setDeleteModal({ show: true, docId, filename });
  };

  const confirmDelete = async () => {
    try {
      await documents.delete(selectedChatbot.id, deleteModal.docId);
      setFiles(files.filter(f => f.id !== deleteModal.docId));
      setDeleteModal({ show: false, docId: null, filename: '' });
      showNotification('Document deleted successfully!');
    } catch (error) {
      showNotification('Failed to delete document: ' + (error.response?.data?.error || 'Unknown error'), 'error');
    }
  };

  const cancelDelete = () => {
    setDeleteModal({ show: false, docId: null, filename: '' });
  };

  const handleUpdateChatbot = async () => {
    if (!selectedChatbot) return;
    
    try {
      await chatbots.update(selectedChatbot.id, {
        name: chatbotName,
        color: chatbotColor,
        welcomeMessage: welcomeMessage
      });
      showNotification('Chatbot settings updated successfully!');
      loadChatbots();
    } catch (error) {
      showNotification('Failed to update chatbot: ' + (error.response?.data?.error || 'Unknown error'), 'error');
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const handleDeploy = async () => {
    console.log('Deploy clicked', { selectedChatbot, filesCount: files.length });
    if (!selectedChatbot) {
      showNotification('Please create a chatbot before deploying', 'warning');
      return;
    }
    
    if (files.length === 0) {
      showNotification('Please upload at least one document before deploying your chatbot', 'warning');
      return;
    }

    try {
      console.log('Sending deploy request for chatbot:', selectedChatbot.id);
      const response = await chatbots.update(selectedChatbot.id, { isDeployed: true });
      console.log('Deploy response:', response);
      setSelectedChatbot(response.data);
      showNotification('Chatbot deployed successfully! Your widget is now live.');
      await loadChatbots(true);
    } catch (error) {
      console.error('Deploy error:', error);
      showNotification('Failed to deploy chatbot: ' + (error.response?.data?.error || error.message || 'Unknown error'), 'error');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setAuth(false);
    navigate('/login');
  };

  const widgetCode = selectedChatbot ? `<!-- Chatbot Widget -->
  <script>
    window.chatbotConfig = {
      chatbotId: "${selectedChatbot.id}",
      apiUrl: "${process.env.REACT_APP_API_URL || window.location.origin}"
    };
  </script>
  <script src="${process.env.REACT_APP_API_URL || window.location.origin}/widget.js"></script>` : '';
  
  const handleCopyCode = () => {
    navigator.clipboard.writeText(widgetCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {/* Background Image */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: '0',
          overflow: 'hidden'
        }}>
          <img
            src={heroBackground}
            alt="Background"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block'
            }}
          />
          {/* Gradient Overlay */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(to right bottom, rgba(249, 250, 251, 0.9), rgba(249, 250, 251, 0.7), rgba(249, 250, 251, 0.9))'
          }}></div>
        </div>
        <div style={{ position: 'relative', zIndex: '1', color: 'white', fontSize: '18px' }}>Loading...</div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      position: 'relative',
      padding: '40px 20px'
    }}>
      {/* Navigation Bar with Logo */}
      <nav style={{
        position: 'relative',
        zIndex: '2',
        maxWidth: '1200px',
        width: '100%',
        margin: '0 auto 20px auto',
        padding: '0 24px'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              transition: 'opacity 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.opacity = '0.8'}
            onMouseLeave={(e) => e.target.style.opacity = '1'}
            aria-label="Go to top of page"
          >
            <img
              src={logo}
              alt="Logo"
              style={{
                width: '240px',
                height: 'auto',
                objectFit: 'contain',
                display: 'block'
              }}
            />
          </button>
        </div>
      </nav>

      {/* Background Image */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: '0',
        overflow: 'hidden'
      }}>
        <img
          src={heroBackground}
          alt="Background"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block'
          }}
        />
        {/* Gradient Overlay */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(to right bottom, rgba(249, 250, 251, 0.9), rgba(249, 250, 251, 0.7), rgba(249, 250, 251, 0.9))'
        }}></div>
      </div>
      {/* Inline Notification */}
      {notification && (
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto 24px auto',
          background: notification.type === 'error' ? 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)' : 
                     notification.type === 'warning' ? 'linear-gradient(135deg, #fef3c7 0%, #fed7aa 100%)' : 
                     'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)',
          border: `2px solid ${notification.type === 'error' ? '#f87171' : 
                               notification.type === 'warning' ? '#fbbf24' : '#667eea'}`,
          borderRadius: '12px',
          padding: '18px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: notification.type === 'error' ? '#ef4444' : 
                         notification.type === 'warning' ? '#f59e0b' : '#667eea',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '18px'
            }}>
              {notification.type === 'error' ? '✕' : notification.type === 'warning' ? '⚠' : '✓'}
            </div>
            <span style={{
              color: notification.type === 'error' ? '#7f1d1d' : 
                     notification.type === 'warning' ? '#92400e' : '#4338ca',
              fontSize: '16px',
              fontWeight: '600',
              lineHeight: '1.5'
            }}>
              {notification.message}
            </span>
          </div>
          {(notification.type === 'error' || notification.type === 'warning') && (
            <button
              onClick={clearNotification}
              style={{
                background: notification.type === 'error' ? '#ef4444' : '#f59e0b',
                border: 'none',
                color: 'white',
                fontSize: '16px',
                cursor: 'pointer',
                padding: '8px 12px',
                borderRadius: '6px',
                fontWeight: '600',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.opacity = '0.8'}
              onMouseLeave={(e) => e.target.style.opacity = '1'}
            >
              ×
            </button>
          )}
        </div>
      )}
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        position: 'relative',
        zIndex: '1'
      }}>
        {/* Header */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '32px',
          marginBottom: '24px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px'
          }}>
            <div>
              <div style={{
                fontSize: '18px',
                color: '#667eea',
                fontWeight: '500',
                marginBottom: '8px'
              }}>
                {(() => {
                  const hour = new Date().getHours();
                  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
                  const name = userProfile?.first_name && userProfile?.last_name ? 
                    `${userProfile.first_name.charAt(0).toUpperCase() + userProfile.first_name.slice(1)} ${userProfile.last_name.charAt(0).toUpperCase() + userProfile.last_name.slice(1)}` : 
                    'There';
                  return `${greeting}, ${name}!`;
                })()} 
              </div>
              <h1 style={{
                fontSize: '32px',
                fontWeight: 'bold',
                color: '#1f2937',
                margin: '0 0 8px 0'
              }}>
                ChatBot Dashboard
              </h1>
              <p style={{
                color: '#6b7280',
                margin: 0,
                fontSize: '16px'
              }}>
                Manage your AI-powered chatbots
              </p>
            </div>
            <button
              onClick={handleLogout}
              style={{
                background: '#ef4444',
                color: 'white',
                padding: '14px 28px',
                border: 'none',
                borderRadius: '16px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'background 0.2s',
                boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)'
              }}
              onMouseEnter={(e) => e.target.style.background = '#dc2626'}
              onMouseLeave={(e) => e.target.style.background = '#ef4444'}
            >
              Logout
            </button>
          </div>

          {/* Tabs */}
          <div style={{
            display: 'flex',
            gap: '8px',
            borderBottom: '2px solid #e5e7eb',
            marginTop: '24px'
          }}>
            {['chatbots', 'upload', 'customize', 'analytics', 'conversations', 'deploy'].map(tab => (
              <button
                key={tab}
                onClick={() => handleTabChange(tab)}
                className={activeTab === tab ? 'bg-gradient-accent' : ''}
                style={{
                  padding: '12px 24px',
                  background: activeTab === tab ? undefined : 'transparent',
                  color: activeTab === tab ? 'white' : '#6b7280',
                  border: 'none',
                  borderRadius: '8px 8px 0 0',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  textTransform: 'capitalize',
                  transition: 'all 0.2s'
                }}
              >
                {tab === 'upload' ? 'Documents' : tab === 'analytics' ? 'Analytics' : tab === 'conversations' ? 'Chat History' : tab}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '32px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
        }}>
          {/* Chatbots Tab */}
          {activeTab === 'chatbots' && (
            <div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '24px'
              }}>
                <h2 style={{
                  fontSize: '24px',
                  fontWeight: 'bold',
                  color: '#1f2937',
                  margin: 0
                }}>
                  Your Chatbots
                </h2>
                {allChatbots.length === 0 && (
                  <button
                    onClick={() => setShowNewBotForm(!showNewBotForm)}
                    className='bg-gradient-accent'
                    style={{
                      color: 'white',
                      padding: '14px 28px',
                      border: 'none',
                      borderRadius: '16px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
                    }}
                  >
                    + New Chatbot
                  </button>
                )}
              </div>

              {showNewBotForm && (
                <div style={{
                  background: '#f9fafb',
                  padding: '24px',
                  borderRadius: '12px',
                  marginBottom: '24px',
                  border: '1px solid #e5e7eb'
                }}>
                  <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', color: '#1f2937' }}>
                    Create New Chatbot
                  </h3>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#374151',
                      marginBottom: '8px'
                    }}>
                      Chatbot Name
                    </label>
                    <input
                      type="text"
                      value={chatbotName}
                      onChange={(e) => setChatbotName(e.target.value)}
                      placeholder="e.g., Customer Support Bot"
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        fontSize: '16px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#374151',
                      marginBottom: '8px'
                    }}>
                      Theme Color
                    </label>
                    <input
                      type="color"
                      value={chatbotColor}
                      onChange={(e) => setChatbotColor(e.target.value)}
                      style={{
                        width: '100px',
                        height: '44px',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        cursor: 'pointer'
                      }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button
                      onClick={handleCreateChatbot}
                      className='bg-gradient-accent'
                      style={{
                        color: 'white',
                        padding: '14px 28px',
                        border: 'none',
                        borderRadius: '16px',
                        fontSize: '16px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
                      }}
                    >
                      Create
                    </button>
                    <button
                      onClick={() => setShowNewBotForm(false)}
                      style={{
                        background: '#e5e7eb',
                        color: '#374151',
                        padding: '14px 28px',
                        border: 'none',
                        borderRadius: '16px',
                        fontSize: '16px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {allChatbots.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '48px', color: '#6b7280' }}>
                  <p style={{ fontSize: '18px', marginBottom: '16px' }}>No chatbots yet</p>
                  <p>Click "New Chatbot" to create your first one</p>
                </div>
              ) : (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
                    {allChatbots.map(bot => (
                      <div
                        key={bot.id}
                        onClick={() => setSelectedChatbot(bot)}
                        style={{
                          padding: '24px',
                          border: selectedChatbot?.id === bot.id ? `3px solid ${bot.color}` : '1px solid #e5e7eb',
                          borderRadius: '12px',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          background: selectedChatbot?.id === bot.id ? '#f0f9ff' : 'white'
                        }}
                      >
                        <div style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '12px',
                          background: bot.color,
                          marginBottom: '16px'
                        }}></div>
                        <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px', color: '#1f2937' }}>
                          {bot.name}
                        </h3>
                        <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
                          {bot.is_deployed ? '✓ Deployed' : 'Not deployed'}
                        </p>
                      </div>
                    ))}
                  </div>
                  
                  {(() => {
                    // Debug: Always show a message to test
                    if (!userProfile) {
                      return (
                        <div style={{
                          marginTop: '24px',
                          padding: '16px',
                          background: '#e0e7ff',
                          border: '1px solid #c7d2fe',
                          borderRadius: '8px',
                          color: '#3730a3',
                          textAlign: 'center'
                        }}>
                          Loading trial information...
                        </div>
                      );
                    }
                    
                    if (!userProfile.trial_ends_at) {
                      return (
                        <div style={{
                          marginTop: '24px',
                          padding: '16px',
                          background: '#e0e7ff',
                          border: '1px solid #c7d2fe',
                          borderRadius: '8px',
                          color: '#3730a3',
                          textAlign: 'center'
                        }}>
                          No trial data found.
                        </div>
                      );
                    }
                    
                    const trialEndsAt = new Date(userProfile.trial_ends_at);
                    const now = new Date();
                    const daysLeft = Math.ceil((trialEndsAt - now) / (1000 * 60 * 60 * 24));
                    
                    if (daysLeft <= 0) {
                      return (
                        <div style={{
                          marginTop: '24px',
                          padding: '16px',
                          background: '#fee2e2',
                          border: '1px solid #fecaca',
                          borderRadius: '8px',
                          color: '#991b1b',
                          textAlign: 'center'
                        }}>
                          ⚠️ Your trial has expired. Upgrade to continue using your chatbots.
                        </div>
                      );
                    }
                    
                    return (
                      <div style={{
                        marginTop: '24px',
                        padding: '16px',
                        background: '#fef3c7',
                        border: '1px solid #fed7aa',
                        borderRadius: '8px',
                        color: '#92400e',
                        textAlign: 'center'
                      }}>
                        ⏰ Your trial ends in {daysLeft} day{daysLeft !== 1 ? 's' : ''}.
                      </div>
                    );
                  })()} 
                </>
              )}
            </div>
          )}

          {/* Upload Documents Tab */}
          {activeTab === 'upload' && selectedChatbot && (
            <div>
              <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#1f2937', marginBottom: '8px' }}>
                Upload Documents
              </h2>
              <p style={{ color: '#6b7280', marginBottom: '8px' }}>
                Upload FAQs, PDFs, or text documents to train your chatbot
              </p>
              <p style={{ color: '#667eea', marginBottom: '8px', fontSize: '14px', fontWeight: '600' }}>
                {files.length}/10 documents uploaded {files.length >= 10 && '(Limit reached)'}
              </p>
              <div style={{
                background: '#eff6ff',
                border: '1px solid #bfdbfe',
                borderRadius: '8px',
                padding: '12px 16px',
                marginBottom: '24px',
                fontSize: '13px',
                color: '#1e40af'
              }}>
                <strong>💡 Tip:</strong> For best results, use TXT or DOCX files. PDFs must be text-based (not scanned images).
              </div>

              <label htmlFor="file-upload">
                <div style={{
                  border: '2px dashed #d1d5db',
                  borderRadius: '12px',
                  padding: '48px',
                  textAlign: 'center',
                  cursor: files.length >= 10 ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  marginBottom: '24px',
                  opacity: files.length >= 10 ? 0.5 : 1
                }}
                onMouseEnter={(e) => {
                  if (files.length < 10) {
                    e.currentTarget.style.borderColor = '#667eea';
                    e.currentTarget.style.background = '#f0f9ff';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#d1d5db';
                  e.currentTarget.style.background = 'transparent';
                }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>📄</div>
                  <p style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                    {files.length >= 10 ? 'Document limit reached' : 'Drop files here or click to upload'}
                  </p>
                  <p style={{ fontSize: '14px', color: '#6b7280' }}>
                    {files.length >= 10 ? 'Delete a document to upload a new one' : 'TXT, DOCX recommended • PDF (text-based only) • Max 10MB'}
                  </p>
                </div>
                <input
                  id="file-upload"
                  type="file"
                  accept=".pdf,.txt,.doc,.docx,.csv"
                  onChange={handleFileUpload}
                  disabled={files.length >= 10}
                  style={{ display: 'none' }}
                />
              </label>

              {files.length > 0 && (
                <div>
                  <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', color: '#1f2937' }}>
                    Uploaded Files ({files.length})
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {files.map(file => (
                      <div key={file.id} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '16px',
                        background: '#f9fafb',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb'
                      }}>
                        <div>
                          <p style={{ fontWeight: '600', color: '#1f2937', margin: '0 0 4px 0' }}>
                            {file.filename}
                          </p>
                          <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
                            {(file.file_size / 1024).toFixed(2)} KB • {file.status}
                          </p>
                        </div>
                        <button
                          onClick={() => handleDeleteFile(file.id, file.filename)}
                          style={{
                            background: '#fee2e2',
                            color: '#dc2626',
                            padding: '8px 16px',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontWeight: '600'
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Customize Tab */}
          {activeTab === 'customize' && selectedChatbot && (
            <div>
              <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#1f2937', marginBottom: '8px' }}>
                Customize Chatbot
              </h2>
              <p style={{ color: '#6b7280', marginBottom: '24px' }}>
                Personalize your chatbot's appearance
              </p>

              <div style={{ maxWidth: '600px' }}>
                <div style={{ marginBottom: '24px' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '8px'
                  }}>
                    Chatbot Name
                  </label>
                  <input
                    type="text"
                    value={chatbotName}
                    onChange={(e) => setChatbotName(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '16px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '8px'
                  }}>
                    Theme Color
                  </label>
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <input
                      type="color"
                      value={chatbotColor}
                      onChange={(e) => setChatbotColor(e.target.value)}
                      style={{
                        width: '80px',
                        height: '44px',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        cursor: 'pointer'
                      }}
                    />
                    <input
                      type="text"
                      value={chatbotColor}
                      onChange={(e) => setChatbotColor(e.target.value)}
                      style={{
                        flex: 1,
                        padding: '12px 16px',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        fontSize: '16px'
                      }}
                    />
                  </div>
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '8px'
                  }}>
                    Welcome Message
                  </label>
                  <textarea
                    value={welcomeMessage}
                    onChange={(e) => setWelcomeMessage(e.target.value)}
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontFamily: 'inherit',
                      boxSizing: 'border-box',
                      resize: 'vertical'
                    }}
                  />
                </div>

                <button
                  onClick={handleUpdateChatbot}
                  className='bg-gradient-accent'
                  style={{
                    color: 'white',
                    padding: '14px 28px',
                    border: 'none',
                    borderRadius: '16px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
                  }}
                >
                  Save Changes
                </button>
                
                {notification && activeTab === 'customize' && (
                  <div style={{
                    marginTop: '16px',
                    padding: '12px 16px',
                    background: notification.type === 'error' ? '#fee2e2' : '#e0e7ff',
                    border: `1px solid ${notification.type === 'error' ? '#f87171' : '#667eea'}`,
                    borderRadius: '8px',
                    color: notification.type === 'error' ? '#991b1b' : '#4338ca',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}>
                    {notification.message}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Analytics Tab */}
          {activeTab === 'analytics' && selectedChatbot && (
            <Analytics chatbotId={selectedChatbot.id} />
          )}

          {/* Conversations Tab */}
          {activeTab === 'conversations' && selectedChatbot && (
            <Conversations chatbotId={selectedChatbot.id} />
          )}

          {/* Deploy Tab */}
          {activeTab === 'deploy' && selectedChatbot && (
            <div>
              <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#1f2937', marginBottom: '8px' }}>
                Deploy Your Chatbot
              </h2>
              <p style={{ color: '#6b7280', marginBottom: '24px' }}>
                Copy and paste this code into your website
              </p>

              {files.length === 0 && (
                <div style={{
                  marginBottom: '24px',
                  padding: '16px',
                  background: '#fef3c7',
                  border: '1px solid #fed7aa',
                  borderRadius: '8px',
                  color: '#92400e',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <span style={{ fontSize: '20px' }}>⚠</span>
                  <span>Please upload at least one document to deploy your chatbot</span>
                </div>
              )}

              <button
                onClick={handleDeploy}
                disabled={selectedChatbot?.is_deployed || files.length === 0}
                className={!selectedChatbot?.is_deployed && files.length > 0 ? 'bg-gradient-accent' : ''}
                style={{
                  background: (selectedChatbot?.is_deployed || files.length === 0) ? '#9ca3af' : undefined,
                  color: 'white',
                  padding: '14px 28px',
                  border: 'none',
                  borderRadius: '16px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: (selectedChatbot?.is_deployed || files.length === 0) ? 'not-allowed' : 'pointer',
                  marginBottom: '24px',
                  opacity: (selectedChatbot?.is_deployed || files.length === 0) ? 0.6 : 1,
                  boxShadow: (!selectedChatbot?.is_deployed && files.length > 0) ? '0 4px 12px rgba(102, 126, 234, 0.3)' : 'none'
                }}
              >
                {selectedChatbot?.is_deployed ? 'Already Deployed' : 'Deploy Chatbot'}
              </button>

              <div style={{
                background: '#1f2937',
                borderRadius: '12px',
                padding: '24px',
                position: 'relative',
                marginBottom: '24px'
              }}>
                <button
                  onClick={handleCopyCode}
                  style={{
                    position: 'absolute',
                    top: '16px',
                    right: '16px',
                    background: '#374151',
                    color: 'white',
                    padding: '8px 16px',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  {copied ? '✓ Copied!' : 'Copy Code'}
                </button>
                <pre style={{
                  color: '#10b981',
                  fontSize: '14px',
                  overflow: 'auto',
                  margin: 0,
                  fontFamily: 'monospace',
                  lineHeight: '1.6'
                }}>
                  <code>{widgetCode}</code>
                </pre>
              </div>

              <div style={{
                background: '#eff6ff',
                border: '1px solid #bfdbfe',
                borderRadius: '12px',
                padding: '24px'
              }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1e40af', marginBottom: '12px' }}>
                  📋 Quick Start Guide
                </h3>
                <ol style={{ color: '#1e40af', paddingLeft: '20px', margin: 0, lineHeight: '1.8' }}>
                  <li>Copy the widget code above</li>
                  <li>Paste it into your website's HTML before the closing &lt;/body&gt; tag</li>
                  <li>Save and publish your website</li>
                  <li>Your chatbot will appear in the bottom-right corner!</li>
                </ol>
              </div>
            </div>
          )}

          {!selectedChatbot && activeTab !== 'chatbots' && (
            <div style={{ textAlign: 'center', padding: '48px', color: '#6b7280' }}>
              <p style={{ fontSize: '18px' }}>Please select or create a chatbot first</p>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModal.show && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)'
          }}>
            <div style={{
              textAlign: 'center',
              marginBottom: '24px'
            }}>
              <div style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                background: '#fee2e2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px auto',
                fontSize: '24px',
                color: '#dc2626'
              }}>
                ⚠️
              </div>
              <h3 style={{
                fontSize: '20px',
                fontWeight: '600',
                color: '#1f2937',
                margin: '0 0 8px 0'
              }}>
                Delete Document
              </h3>
              <p style={{
                color: '#6b7280',
                margin: 0,
                lineHeight: '1.5'
              }}>
                Are you sure you want to delete <strong>"{deleteModal.filename}"</strong>? This action cannot be undone and will permanently remove this document from your chatbot's training data.
              </p>
            </div>
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'center'
            }}>
              <button
                onClick={cancelDelete}
                style={{
                  background: '#f3f4f6',
                  color: '#374151',
                  padding: '12px 24px',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.background = '#e5e7eb'}
                onMouseLeave={(e) => e.target.style.background = '#f3f4f6'}
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                style={{
                  background: '#dc2626',
                  color: 'white',
                  padding: '12px 24px',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.background = '#b91c1c'}
                onMouseLeave={(e) => e.target.style.background = '#dc2626'}
              >
                Delete Document
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Add CSS animation for toast
const style = document.createElement('style');
style.textContent = `
  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;
if (!document.head.querySelector('style[data-toast]')) {
  style.setAttribute('data-toast', 'true');
  document.head.appendChild(style);
}

export default Dashboard;